import { useState, useCallback, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  useFetchCurrentUserProfileQuery,
  useUpdateUserProfileMutation,
} from "@/api/clubsApi";
import type { ImageUploadResult } from "@/utils/uploadUtils";
import { useAuth } from "@/components/providers/AuthProvider";
import { Icon } from "@/components/ui/Icon";
import { StepIndicator } from "./StepIndicator";
import { OnboardingIntroStep } from "./OnboardingIntroStep";
import { OnboardingBasicInfoStep } from "./OnboardingBasicInfoStep";
import { OnboardingPhotoStep } from "./OnboardingPhotoStep";
import { OnboardingInterestsStep } from "./OnboardingInterestsStep";
import { OnboardingLocationStep } from "./OnboardingLocationStep";
import { OnboardingStartClubStep } from "./OnboardingStartClubStep";

const TOTAL_STEPS = 6;

interface OnboardingModalProps {
  closeOnboarding: () => void;
}

export function OnboardingModal({ closeOnboarding }: OnboardingModalProps) {
  const { user, signOut } = useAuth();
  const userId = user?.id ?? "";

  // Fetch profile with topics for prefilling
  const { data: userProfile } = useFetchCurrentUserProfileQuery(
    userId ? { id: userId, includeTopics: true } : "",
    { skip: !userId }
  );

  const [updateProfile] = useUpdateUserProfileMutation();

  // Step navigation
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);

  // Form state (lifted from steps)
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState<string | null>(null);

  // Photo state
  const [photoUri, setPhotoUri] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<ImageUploadResult | null>(
    null
  );
  const [isUploading, setIsUploading] = useState(false);

  // Location state
  const [, setLocationPermissionGranted] = useState(false);

  // Prefill from profile (once)
  const hasPrefilledRef = useRef(false);
  const originalUsernameRef = useRef("");

  useEffect(() => {
    if (userProfile && !hasPrefilledRef.current) {
      let didPrefill = false;
      if (userProfile.first_name) {
        setFirstName(userProfile.first_name);
        didPrefill = true;
      }
      if (userProfile.last_name) {
        setLastName(userProfile.last_name);
        didPrefill = true;
      }
      if (userProfile.username) {
        setUsername(userProfile.username);
        originalUsernameRef.current = userProfile.username;
        didPrefill = true;
      }
      if (userProfile.avatar_medium) {
        setPhotoUri(userProfile.avatar_medium);
        didPrefill = true;
      }
      if (didPrefill) hasPrefilledRef.current = true;
    }
  }, [userProfile]);

  // Navigation handlers
  const handleContinue = useCallback(() => {
    setDirection(1);
    setCurrentStep((prev) => Math.min(prev + 1, TOTAL_STEPS - 1));
  }, []);

  const handleBack = useCallback(() => {
    setDirection(-1);
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleBasicInfoBack = useCallback(() => {
    if (!username.trim() && originalUsernameRef.current) {
      setUsername(originalUsernameRef.current);
    }
    handleBack();
  }, [username, handleBack]);

  // Complete onboarding
  const completeOnboarding = useCallback(
    async (prefillName: string | null = null) => {
      if (!userId) return;

      try {
        await updateProfile({
          id: userId,
          onboarding_complete: true,
        }).unwrap();

        closeOnboarding();

        // Navigate to club creation if an option was selected
        if (prefillName !== null) {
          const params = prefillName
            ? `?prefillName=${encodeURIComponent(prefillName)}`
            : "";
          window.location.href = `/clubs/create${params}`;
        }
      } catch (error) {
        console.error("[Onboarding] Failed to complete:", error);
        window.alert("Failed to save. Please try again.");
      }
    },
    [userId, updateProfile, closeOnboarding]
  );

  const handleSkip = useCallback(async () => {
    await completeOnboarding(null);
  }, [completeOnboarding]);

  const handleSignOut = useCallback(async () => {
    await signOut();
  }, [signOut]);

  // Animation variants
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.95,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.95,
    }),
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <OnboardingIntroStep
            onContinue={handleContinue}
            onSignOut={handleSignOut}
          />
        );
      case 1:
        return (
          <OnboardingBasicInfoStep
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            username={username}
            setUsername={setUsername}
            usernameError={usernameError}
            setUsernameError={setUsernameError}
            userId={userId}
            onContinue={handleContinue}
          />
        );
      case 2:
        return (
          <OnboardingPhotoStep
            photoUri={photoUri}
            setPhotoUri={setPhotoUri}
            uploadResult={uploadResult}
            setUploadResult={setUploadResult}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            userId={userId}
            onContinue={handleContinue}
          />
        );
      case 3:
        return (
          <OnboardingInterestsStep
            userId={userId}
            userTopics={userProfile?.user_topics}
            onContinue={handleContinue}
          />
        );
      case 4:
        return (
          <OnboardingLocationStep
            userId={userId}
            setLocationPermissionGranted={setLocationPermissionGranted}
            onContinue={handleContinue}
          />
        );
      case 5:
        return (
          <OnboardingStartClubStep
            onOptionSelected={completeOnboarding}
            onSkip={handleSkip}
            userLocationObject={userProfile?.location_object}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col w-full h-full">
      {/* Header: back button */}
      <div className="flex-shrink-0 flex items-center px-6 py-4">
        <div className="w-8">
          {currentStep > 0 && (
            <button
              onClick={currentStep === 1 ? handleBasicInfoBack : handleBack}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface transition-colors"
            >
              <Icon name="angle-left" size={18} className="text-text-primary" />
            </button>
          )}
        </div>
      </div>

      {/* Step content with animations */}
      <div className="flex-1 min-h-0 relative overflow-hidden">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer: step indicator */}
      <div className="flex-shrink-0 flex items-center justify-center py-4">
        <StepIndicator currentStep={currentStep} totalSteps={TOTAL_STEPS} />
      </div>
    </div>
  );
}
